// libs
import React, { memo, useMemo, useState } from 'react';

// components
import IOElement from 'shared/components/IOElement';
import Image from 'shared/components/ImageV2';
import Description from '../Description';

// context
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// styles
import { Container, TagWrapper, Text, ImageContainer } from './styles';
import { Tag as ITag } from 'shared/layouts/DetailsPage/components/ProductInfo/components/ProductDescription/components/ResponsibleTags/types';

import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// defs
interface TagProps {
  tagInfo: ITag;
  showAnimation: boolean;
  onAnimationEnd: VoidFunction;
  index: number;
  setAnimationIndex: (index: number) => void;
}

const IO_OPTIONS = { threshold: 0.8 };
const IMAGE_DIMENSION = {
  WIDTH: 24,
  HEIGHT: 24,
  ASPECT_RATIO: 1,
};

function Tag({ showAnimation, onAnimationEnd, index, setAnimationIndex, tagInfo }: TagProps) {
  const { deviceWidth } = useResponsiveContext();
  const containerWidth = useMemo(() => deviceWidth / 4, [deviceWidth]);
  const { animationDuration, animationInterval, enabled } = useRemoteConfig('PDPResponsibleTags');

  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  if (!enabled) {
    return null;
  }
  const onAnimationComplete = () => {
    setIsAnimationComplete(true);
    onAnimationEnd();
  };

  const onVisible = () => {
    // manually set index if user scrolls to the end without waiting for animation
    if (!showAnimation) {
      setAnimationIndex(index);
    }

    // start animation if the element is in view port
    if (showAnimation && !isAnimationComplete) {
      setAnimationIndex(index);
      setShouldAnimate(true);
    }
  };

  const handleTagClicked = () => {
    tagInfo.description && setShowDescription(true);
  };

  return (
    <>
      <Container containerWidth={containerWidth} onClick={handleTagClicked}>
        <TagWrapper>
          <IOElement onVisible={onVisible} options={IO_OPTIONS}>
            <ImageContainer
              shouldAnimate={shouldAnimate}
              onTransitionEnd={onAnimationComplete}
              animationDuration={animationDuration}
              animationDelay={animationInterval}
            >
              <Image
                src={tagInfo.image_url}
                alt={tagInfo.label}
                width={IMAGE_DIMENSION.WIDTH}
                height={IMAGE_DIMENSION.HEIGHT}
                aspectRatio={IMAGE_DIMENSION.ASPECT_RATIO}
                placeholderColor="transparent"
              />
            </ImageContainer>
          </IOElement>
        </TagWrapper>
        <Text>{tagInfo.label}</Text>
      </Container>

      {showDescription && (
        <Description
          imageUrl={tagInfo.image_url}
          label={tagInfo.label}
          description={tagInfo.description}
          onHide={() => setShowDescription(false)}
        />
      )}
    </>
  );
}

export default memo(Tag);
